import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import IconButton from 'fe-design-base/molecules/IconButton';
import Tabs from 'fe-design-base/molecules/Tabs';

import {
  fetchNotifications,
  toggleNotificationsShown,
} from 'actions/notifications';

import { buildGetDrawerOpen } from 'selectors/drawers';
import { getLeftNavCollapsed } from 'selectors/nav';
import {
  getAlertsItems,
  getHasMoreAlerts,
  getHasMoreRequests,
  getIsFetchingNotifications,
  getRequestsItems,
} from 'selectors/notifications';
import {
  getCurrentUserIsManagerial,
  getRequestsFeedExperiment,
  getShouldShowSimplifiedLeftNav,
} from 'selectors/session';

import { NOTIFICATIONS_DRAWER_KEY } from 'features/drawers/keys';
import { enrollEntityInExperiment } from 'features/experiments/actions';
import { MSG_DRAWER_WIDTH } from 'features/messenger/constants';
import {
  LEFT_NAV_DRAWER_PADDING,
  ZINDEX_LEFT_NAV_DRAWER_LAYER,
  ZINDEX_LEFTNAV_DRAWER_WRAPPERS,
} from 'features/nav/LeftNav/constants';
import { MyRequests } from 'features/notifications/components/MyRequests';
import { NotificationsHeader } from 'features/notifications/components/NotificationsHeader';
import { TeamRequests } from 'features/notifications/components/TeamRequests';
import NotificationsView from 'features/notifications/NotificationsView/NotificationsView';
import {
  EVENT_ACTIONS,
  trackNotificationEvents,
} from 'features/notifications/tracking';

import Drawer from 'components/Drawer';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';

import NotificationsList from './NotificationList';

const { cxEl } = cxHelpers('NotificationView');

const NAV_HEADERS = [
  {
    value: 'requests',
    label: toI18n('notifications.requests.title'),
  },
  {
    value: 'alerts',
    label: toI18n('notifications.alerts.title'),
  },
];

const EMPLOYEE_NAV_HEADERS = [NAV_HEADERS[0]];

const AlertsDrawer = () => {
  const dispatch = useDispatch();
  const requestsFeedEnabled = useSelector(getRequestsFeedExperiment);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const [tabValue, setTabValue] = useState<string>(
    requestsFeedEnabled ? 'team-requests' : 'requests'
  );
  const isManagerial = useSelector(getCurrentUserIsManagerial);
  const shouldShowSimplifiedLeftNav = useSelector(
    getShouldShowSimplifiedLeftNav
  );

  const requestsItems = useSelector(getRequestsItems);
  const alertsItems = useSelector(getAlertsItems);
  const isFetching = useSelector(getIsFetchingNotifications);
  const hasMoreRequests = useSelector(getHasMoreRequests);
  const hasMoreAlerts = useSelector(getHasMoreAlerts);
  const isOpen = useSelector(buildGetDrawerOpen(NOTIFICATIONS_DRAWER_KEY));
  const isCollapsed = useSelector(getLeftNavCollapsed);

  useEffect(() => {
    // this thunk was not typed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    enrollEntityInExperiment(['requests_feed']);
  }, []);

  useEffect(() => {
    if (!isManagerial) {
      if (!requestsFeedEnabled && tabValue !== 'requests') {
        setTabValue('requests');
      } else if (requestsFeedEnabled && tabValue !== 'my-requests') {
        setTabValue('my-requests');
      }
    }
  }, [isManagerial, requestsFeedEnabled, tabValue]);

  const handleClose = useCallback(() => {
    trackNotificationEvents({
      eventAction: EVENT_ACTIONS.CLOSE_CLICKED,
    });
    dispatch(toggleNotificationsShown(false));
  }, [dispatch]);

  const handleAlertsTabClick = useCallback(() => {
    trackNotificationEvents({
      eventAction: EVENT_ACTIONS.ALERTS_CLICKED,
    });
  }, []);

  const handleRequestsTabClick = useCallback(() => {
    trackNotificationEvents({
      eventAction: EVENT_ACTIONS.REQUESTS_CLICKED,
    });
  }, []);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: string | number) => {
      setTabValue(newValue as string);
      const handleTabClick =
        newValue === toI18n('notifications.requests.title')
          ? handleRequestsTabClick
          : handleAlertsTabClick;
      handleTabClick();
    },
    [handleRequestsTabClick, handleAlertsTabClick]
  );

  const getScrollContainer = useCallback(() => scrollContainerRef.current, []);

  const getHeaders = useCallback(
    () =>
      (isManagerial ? NAV_HEADERS : EMPLOYEE_NAV_HEADERS).map(content => {
        const collection =
          content.label === toI18n('notifications.requests.title')
            ? requestsItems
            : alertsItems;

        return {
          value: content.value,
          label: content.label,
          badgeText: collection.size > 0 ? collection.size : undefined,
          badgeVariant: 'loud' as const,
        };
      }),
    [isManagerial, requestsItems, alertsItems]
  );

  const handleFetchNotifications = useCallback(
    (params: any) => {
      if (params?.type === 'alerts' && !isManagerial) return;
      dispatch(fetchNotifications(params));
    },
    [dispatch, isManagerial]
  );

  const renderNotificationsList = useCallback(
    (type: 'requests' | 'alerts', items: any) => (
      <Box key={type}>
        <NotificationsList
          notificationType={type}
          notificationsItems={items}
          fetchNotifications={handleFetchNotifications}
          isFetching={isFetching}
          hasMore={type === 'requests' ? hasMoreRequests : hasMoreAlerts}
          getScrollContainer={getScrollContainer as () => HTMLElement}
          onClose={handleClose}
        />
      </Box>
    ),
    [
      isFetching,
      hasMoreRequests,
      hasMoreAlerts,
      getScrollContainer,
      handleClose,
      handleFetchNotifications,
    ]
  );

  if (shouldShowSimplifiedLeftNav) {
    return (
      <Box
        style={{
          position: 'fixed',
          left: isCollapsed ? 0 : 0,
          top: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          clipPath: `inset(0 0 0 ${isCollapsed ? 77 : 205}px)`,
          zIndex: ZINDEX_LEFTNAV_DRAWER_WRAPPERS,
        }}
      >
        <Drawer
          zIndex={ZINDEX_LEFT_NAV_DRAWER_LAYER}
          className={cxEl('left-nav-alerts-drawer')}
          width={String(MSG_DRAWER_WIDTH)}
          open={isOpen}
          containerStyle={{
            height: `calc(100vh - (2 * ${LEFT_NAV_DRAWER_PADDING}px))`,
            position: 'absolute',
            left: isCollapsed ? 83 : 210,
            top: LEFT_NAV_DRAWER_PADDING,
            bottom: LEFT_NAV_DRAWER_PADDING,
            borderRadius: '16px',
            border: '1px solid var(--mono300, #e6e4d6)',
            pointerEvents: 'auto',
            width: '440px',
            overflowX: 'hidden',
          }}
          alignLeft
        >
          <Box column maxh="100%" ph={12}>
            {requestsFeedEnabled ? (
              <NotificationsHeader
                tabValue={tabValue}
                handleTabChange={handleTabChange}
                handleClose={handleClose}
              />
            ) : (
              <Box row alignItemsCenter gap={8} w="100%">
                <Box grow={1}>
                  <Tabs
                    noPadding
                    onChange={handleTabChange}
                    tabValue={tabValue}
                    options={getHeaders()}
                    scrollable
                  />
                </Box>
                <Box>
                  <IconButton
                    onClick={handleClose}
                    icon="Close"
                    label={toI18n('notifications.close_drawer')}
                    className={cxEl('close-button')}
                    size="small"
                  />
                </Box>
              </Box>
            )}

            <Box scrollY>
              {tabValue === 'requests' &&
                !requestsFeedEnabled &&
                renderNotificationsList('requests', requestsItems)}
              {tabValue === 'team-requests' && requestsFeedEnabled && (
                <TeamRequests />
              )}
              {tabValue === 'my-requests' && requestsFeedEnabled && (
                <MyRequests />
              )}
              {isManagerial &&
                tabValue === 'alerts' &&
                renderNotificationsList('alerts', alertsItems)}
            </Box>
          </Box>
        </Drawer>
      </Box>
    );
  }

  return <NotificationsView />;
};

export default AlertsDrawer;
